import { FC } from "react"
import { Calendar, ChevronDown, Eye, MessageSquare, User } from "lucide-react"
import { Lead, SubscriptionDetails } from "../../../../../types"
import {
  useGetThreadQuery,
  useLeadSmsFollowupMutation
} from "../../../../../redux/queries"
import { useAppDispatch } from "../../../../hooks"
import { pushToast } from "../../../../../redux/slices/notifications"
import BreadCrumbs from "../../../breadcrumbs"
import { LightBtn } from "../../../buttons"
import Dropdown from "../../../dropdown"
import ProfileLogo from "../../../profile-logo"
import BBSwitch from "../../../form/Switch"
import If from "../../../if"
import { formatDateTimeFromTimestamp } from "../../../../utils"
import { LeadRatesNowBadge } from "../../lead-rates-now-badge"

interface Props {
  data: Lead
  bankName?: string
  subscription?: SubscriptionDetails
  onCreateEngagement: (data: Lead) => void
  onEnrollToRateAlerts: (subscription?: SubscriptionDetails) => void
  returnUrl?: string
  brandName?: string
}

const LeadDetailsHeader: FC<Props> = ({
  data,
  subscription,
  bankName,
  onCreateEngagement,
  onEnrollToRateAlerts,
  returnUrl,
  brandName
}) => {
  const dispatch = useAppDispatch()
  const { data: thread, isFetching: isFetchingThread } = useGetThreadQuery(
    { lead_id: data.id },
    { skip: !data.id }
  )
  const [leadSmsFollowup, { isLoading: isSendingSms }] =
    useLeadSmsFollowupMutation()

  const hasThread = !isFetchingThread && !!thread?._id

  const handleSendSMS = async () => {
    if (!data.id) return
    const resp = await leadSmsFollowup({ lead_id: data.id })
    const result = (resp as any)?.data as
      | { status: string; thread_id?: string; message?: string }
      | undefined
    if (result?.status === "success") {
      dispatch(
        pushToast({ msg: "SMS followup sent", timeout: 3000, theme: "success" })
      )
    } else {
      dispatch(
        pushToast({
          msg: result?.message || "Failed to send SMS followup",
          timeout: 4000,
          theme: "danger"
        })
      )
    }
  }

  const fullName =
    [data.first_name, data.last_name].filter(Boolean).join(" ") || data.email

  const breadcrumbs =
    returnUrl && brandName
      ? [
          {
            name: brandName,
            href: returnUrl,
            current: false
          },
          {
            name: "Leads",
            href: returnUrl,
            current: false
          },
          {
            name: data.email,
            href: "#",
            current: true
          }
        ]
      : [
          {
            name: "Website leads",
            href: "/engagements?tab=leads",
            current: false
          },
          {
            name: data.email,
            href: "#",
            current: true
          }
        ]

  return (
    <>
      <BreadCrumbs pages={breadcrumbs} />

      {/* Identity row */}
      <div className="pt-6 pb-5 flex items-start gap-4">
        <ProfileLogo
          label={`${data.first_name.charAt(0)}${data.last_name.charAt(0)}`}
          className="w-14 h-14 shrink-0 text-lg"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <h1 className="text-xl font-semibold text-neutral-900 truncate">
              {fullName}
            </h1>
            <LeadRatesNowBadge source={data.source} />
          </div>
          <div className="text-sm text-neutral-500 mt-0.5">
            {data.email}
            <span className="mx-1.5">&middot;</span>
            {bankName ?? "Unnamed"}
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs text-neutral-400">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {formatDateTimeFromTimestamp(
                data.timestamp,
                "LLL dd 'at' hh:mm a"
              )}
            </span>
            {data.owner && (
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {data.owner}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Action bar */}
      <div className="pb-4 flex items-center gap-2 flex-wrap">
        <If
          isLoading={isFetchingThread}
          loaderSize={4}
          condition={!hasThread}
          then={
            <LightBtn
              type="button"
              className="text-sm h-9"
              onClick={handleSendSMS}
            >
              <If
                isLoading={isSendingSms}
                loaderSize={4}
                loaderClassName="!p-0"
                condition={!isSendingSms}
                then={
                  <>
                    <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                    Send SMS
                  </>
                }
              />
            </LightBtn>
          }
        />
        {hasThread && (
          <LightBtn
            href={`/conversations/sms/${thread!._id}`}
            className="text-sm h-9"
          >
            <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
            View SMS
          </LightBtn>
        )}
        <Dropdown
          align="left"
          menuBtn={
            <div className="inline-flex justify-center items-center py-1.5 px-3 border text-sm font-medium rounded-md focus:outline-none bg-neutral-100 hover:bg-neutral-200 transition-colors h-9">
              Actions <ChevronDown className="h-4 w-4 ml-1" />
            </div>
          }
          items={[
            {
              name: "Push to engagements",
              icon: Eye,
              iconClassName: "text-neutral-400 mr-2",
              onClick: () => onCreateEngagement(data)
            },
            {
              name: (
                <div className="flex items-center justify-between">
                  <span className="mr-2">
                    {subscription?.active === "1" ? "Unenroll" : "Enroll"} to
                    rate alerts
                  </span>
                  <BBSwitch
                    label=""
                    enabled={subscription?.active === "1"}
                    setEnabled={() => onEnrollToRateAlerts(subscription)}
                  />
                </div>
              ),
              hidden: !subscription
            }
          ]}
        />
      </div>
    </>
  )
}

export default LeadDetailsHeader
